import { useState } from "react";
import { TripPlannerMobileMap } from "src/components/Map/TripPlannerMap/TripPlannerMobileMap";
import color from "src/design-system/tokens/color";
import { useAnalyticsPageView } from "src/utils/hooks/useAnalyticsPageView";
import useSearch from "src/utils/hooks/useSearch";
import { useTripTransportIndex } from "src/utils/hooks/useTripTransportIndex";
import styled, { css } from "styled-components";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { spacing } from "src/design-system/tokens/spacing";
import { useAnalyticsCoreExperience } from "../../../analytics/useAnalyticsCoreExperience/useAnalyticsCoreExperience";
import { color as oldColor, screenMinWidth } from "../../../theme";
import {
  type ReturnStage,
  useTypedLocation,
} from "../../../utils/hooks/useTypedLocation";
import { MemoizedTripPlannerDrawer } from "../Drawer/TripPlannerDrawer";
import LargeHeading from "../Heading/LargeHeading";
import LazyReorderTrip from "../ReorderTrip/ReorderTripScreen/LazyReorderTrip";
import { TransportResults } from "../TransportResults/TransportResults";
import { TripPlannerScreenLoaded } from "../TripPlannerScreenLoaded";
import { TripPlannerSkeleton } from "../TripPlannerSkeleton";
import { UserAccountTakeover } from "../UserAccountTakeover/UserAccountTakeover";
import { useTripPlannerComponents } from "../hooks/useTripPlannerComponents";
import { useTripPlannerContext } from "../hooks/useTripPlannerContext";
import { useTripSearchResponse } from "../hooks/useTripSearchResponse";
import { HotelsPromo } from "../HotelsPromo/HotelsPromo";
import CarRentalPromo from "../CarRentalPromo/CarRentalPromo";
import { TripSaveButton } from "../Heading/TripSaveButton";

type Props = {
  returnsFlowLocation?: ReturnStage;
};

export function MultiTripScreen({ returnsFlowLocation }: Props) {
  const location = useTypedLocation();
  const { isMobile } = useScreenMinWidth();
  const transportIndex = useTripTransportIndex();
  const { tripPlannerDetails, apiState } = useTripPlannerContext();
  const components = useTripPlannerComponents();
  const [showSigninTakeover, setShowSigninTakeover] = useState(false);
  const { tripSearchResponse, tripOrigin, tripDestination } =
    useTripSearchResponse();

  const { searchResponse } = useSearch();
  useAnalyticsPageView(
    {
      pagePath: location.pathname + location.search + location.hash,
      pageLocation: window.location.href,
    },
    searchResponse,
    "TripPlanner",
    undefined,
    components,
  );

  useAnalyticsCoreExperience({
    experienceHash: `${location.pathname}:r${location.hash}/`,
    analyticsLabel: "TripPlanner Screen",
  });

  const contentColor = isMobile ? oldColor.grey1 : color.primitive.neutral400;

  const TripPlannerContentDesktopTablet = (
    <>
      <div className="rounded-lg bg-fill-solid">
        <LargeHeading />
        <Content $isReordering={components.includes("ReorderPage")}>
          {components.includes("TripPlannerCards") &&
            apiState.fetchState !== "fetching" &&
            !components.includes("ReorderPage") && <TripPlannerScreenLoaded />}

          {components.includes("ReorderPage") && (
            <LazyReorderTrip places={tripPlannerDetails.places} />
          )}

          {components.includes("TripPlannerSkeleton") && (
            <TripPlannerSkeleton />
          )}
        </Content>
      </div>
      <div className="mb-8 flex flex-col gap-2">
        {tripPlannerDetails.places.length === 2 && (
          <HotelsPromo
            location={tripPlannerDetails.places[1].shortName}
            eventCategory="TripPlanner"
          />
        )}
        <CarRentalPromo />
      </div>
    </>
  );

  const TripPlannerContentMobile = (
    <Content
      $isReordering={components.includes("ReorderPage")}
      $color={contentColor}
    >
      <div className="mb-3">
        <TripSaveButton />
      </div>

      <UserAccountTakeover
        isDropdownOpen={showSigninTakeover}
        handleClose={() => setShowSigninTakeover(false)}
        handleClickOffDropdown={() => setShowSigninTakeover(false)}
      />
      <TripPlannerMobileMap />
      {components.includes("TripPlannerTitle") && (
        <div className="mt-3 mb-3">
          <LargeHeading />
        </div>
      )}
      {components.includes("TripPlannerCards") &&
        apiState.fetchState !== "fetching" &&
        !components.includes("ReorderPage") && <TripPlannerScreenLoaded />}
      {components.includes("ReorderPage") && (
        <LazyReorderTrip places={tripPlannerDetails.places} />
      )}
      {components.includes("TripPlannerSkeleton") && <TripPlannerSkeleton />}
    </Content>
  );

  if (!isMobile) {
    return (
      <TransportResults
        {...{
          transportIndex,
          returnsFlowLocation,
          AllPlaces: TripPlannerContentDesktopTablet,
          searchResponse: tripSearchResponse,
          tripOrigin,
          tripDestination,
        }}
      />
    );
  } else {
    return (
      <>
        {TripPlannerContentMobile}
        {transportIndex !== undefined && (
          <MemoizedTripPlannerDrawer
            returnsFlowLocation={returnsFlowLocation}
            transportIndex={transportIndex}
          />
        )}
      </>
    );
  }
}

const Content = styled.div<{
  $isReordering?: boolean;
  $color?: string;
}>`
  width: 100%;
  background-color: ${(props) => props.$color};
  position: relative;
  padding: ${spacing.xxl} ${spacing.xl};
  // This is the height minus nav, fixed button and safari menu
  min-height: calc(100vh - 55px - 44px - env(safe-area-inset-bottom));

  ${screenMinWidth.sm} {
    max-width: 100%;
    min-height: unset;
    margin-bottom: ${spacing.md};
  }

  ${({ $isReordering }) =>
    $isReordering &&
    css`
      padding-bottom: 64px;

      ${screenMinWidth.sm} {
        min-height: calc(100vh - 56px);
      }
    `}
`;
